import { useState } from 'react';
import { toast } from 'react-toastify';
import { Hospital, EditFormData, InsuranceItem } from '../types/hospital.types';
import { updateHospital } from '../../../Http/Index';
import { initializeEditForm, processFormData } from '../utils/hospitalHelpers';

export const useHospitalEdit = () => {
  const [editingCard, setEditingCard] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<EditFormData>({});
  const [originalData, setOriginalData] = useState<EditFormData>({});
  const [hasChanges, setHasChanges] = useState(false);

  const startEditing = (hospital: Hospital): void => {
    const initialData = initializeEditForm(hospital);
    setEditFormData(initialData);
    setOriginalData(initialData);
    setHasChanges(false);
    setEditingCard(hospital._id);
  };

  const handleInputChange = (field: keyof EditFormData, value: string | boolean | InsuranceItem[]): void => {
    setEditFormData(prev => {
      const newData = { ...prev, [field]: value };
      const changed = JSON.stringify(newData) !== JSON.stringify(originalData);
      setHasChanges(changed);
      return newData;
    });
  };

  const resetEditState = (): void => {
    setEditingCard(null);
    setEditFormData({});
    setOriginalData({});
    setHasChanges(false);
  };

  const handleSave = async (
    hospitalId: string, 
    onSuccess: (hospitalId: string, updatedData: any) => void,
    onRefreshCounts?: () => void
  ): Promise<void> => {
    setSaving(hospitalId);
    try {
      const processedData = processFormData(editFormData);
      await updateHospital(hospitalId, processedData);
      onSuccess(hospitalId, processedData);
      onRefreshCounts?.();
      resetEditState();
      toast.success('Hospital updated successfully!');
    } catch (error) {
      console.error('Error updating hospital:', error);
      toast.error('Failed to update hospital. Please try again.');
    } finally {
      setSaving(null);
    }
  };

  const toggleEdit = async (
    hospital: Hospital,
    onSave: (hospitalId: string, updatedData: any) => void,
    onRefreshCounts?: () => void
  ): Promise<void> => {
    if (editingCard === hospital._id) {
      await handleSave(hospital._id, onSave, onRefreshCounts);
    } else {
      startEditing(hospital);
    }
  };

  return {
    editingCard,
    saving,
    editFormData,
    hasChanges,
    handleInputChange,
    toggleEdit,
    resetEditState
  };
};